// AcademyPage.jsx — başlangıç yol haritası, temel strateji kavramları ve sık yapılan hatalar.
import React from 'react';
import { Link } from 'react-router-dom';
import Seo, { breadcrumbLd } from '../components/Seo';
import PageHeader from '../components/PageHeader';
import { useLang } from '../i18n';

const C = {
  tr: {
    title: 'Şogi Akademi',
    seoDesc: 'Şogiye yeni başlayanlar için yol haritası: kurallar, ilk oyunlar, tsume bulmacaları, kale yapıları (Yagura, Mino, Anaguma), açılış fikirleri ve sık yapılan hatalar.',
    lead: 'Sıfırdan şogi öğrenmek için adım adım yol haritası, temel strateji kavramları ve yeni başlayanların sık yaptığı hatalar.',
    pathTitle: 'Yol haritası',
    path: [
      ['Kuralları öğren', 'Amacı ve bir hamlenin nasıl yapıldığını oku.', 'howto'],
      ['Taşları tanı', '8 taşın hareketlerini ve terfi edince ne olduklarını ezberle.', 'pieces'],
      ['Özel kurallar', 'Elden taş sürme, nifu ve uchifuzume — şogiyi şogi yapan kurallar.', 'rules'],
      ['İlk oyunlar', "Bilgisayara karşı Kolay seviyede oyna; sonra Orta'ya geç.", 'play'],
      ['Bulmaca çöz', 'Her gün birkaç 1 ve 3 hamlede mat bulmacası çöz.', 'puzzles'],
      ['Rakip bul', "Arena'da gerçek oyunculara karşı oyna.", 'arena'],
    ],
    conceptsTitle: 'Temel strateji kavramları',
    concepts: [
      ['Kale (kakoi) kur', 'Şahını oyunun başında birkaç hamlede altın ve gümüşlerle çevir. Yagura (statik kale açılışlarında), Mino (yan kaleli oyunlarda) ve Anaguma (en sağlam, köşe kalesi) en bilinen yapılardır.'],
      ['Statik kale mi, kaydırılmış kale mi?', 'İbisha (statik kale) oyuncuları kaleyi başlangıç sütununda tutup sağ kanattan saldırır. Furibisha (kaydırılmış kale) oyuncuları kaleyi sola kaydırıp şahı sağa yerleştirir. İkisini de dene, tarzına uyanı seç.'],
      ['Eldeki taş bir silahtır', 'Elinde taş varken her boş kare olası bir saldırı noktasıdır. Özellikle eldeki piyade ve altın, mat ağlarının temel taşlarıdır.'],
      ['Tokin değerlidir', 'Terfi eden piyade altın kadar güçlü saldırır; alındığında rakibe sadece bir piyade verir. Rakip tarafa piyade sokmanın yollarını ara.'],
      ['Değiş tokuşu hesapla', 'Satrançtan farklı olarak alınan taş rakibin eline geçer. Bir taşı alırken rakibin o taşla neler yapabileceğini düşün.'],
    ],
    mistakesTitle: 'Sık yapılan hatalar',
    mistakes: [
      'Şahı başlangıç karesinde bırakıp kale kurmadan saldırmak.',
      'Aynı sütuna ikinci piyadeyi sürmeye çalışmak (nifu).',
      'Fil hattını açık bırakıp rakibin fil değişimiyle elden fil sürmesine izin vermek.',
      'Atı ve mızrağı erken ilerletmek: geri dönemezler.',
      'Rakibin elindeki taşları unutmak: her sıra onları kontrol et.',
    ],
    cta: 'Bulmacalarla pratik yap',
  },
  en: {
    title: 'Shogi Academy',
    seoDesc: 'A beginner roadmap for shogi: rules, first games, tsume puzzles, castles (Yagura, Mino, Anaguma), opening ideas and common mistakes.',
    lead: 'A step-by-step roadmap for learning shogi from scratch, core strategy ideas and the mistakes beginners make most often.',
    pathTitle: 'Roadmap',
    path: [
      ['Learn the basics', 'Read the goal of the game and how a move works.', 'howto'],
      ['Know the pieces', 'Learn how the 8 pieces move and what they become when promoted.', 'pieces'],
      ['Special rules', 'Drops, nifu and uchifuzume — the rules that make shogi unique.', 'rules'],
      ['First games', 'Play the computer on Easy, then move up to Medium.', 'play'],
      ['Solve puzzles', 'Solve a few mate-in-1 and mate-in-3 problems every day.', 'puzzles'],
      ['Find opponents', 'Play real people in the Arena.', 'arena'],
    ],
    conceptsTitle: 'Core strategy ideas',
    concepts: [
      ['Build a castle (kakoi)', 'Early in the game, wrap your king in golds and silvers over a few moves. Yagura (static rook games), Mino (ranging rook games) and Anaguma (the sturdiest, corner castle) are the best known.'],
      ['Static rook or ranging rook?', 'Ibisha (static rook) players keep the rook on its starting file and attack on the right. Furibisha (ranging rook) players swing the rook left and castle on the right. Try both and pick the one that suits you.'],
      ['Pieces in hand are weapons', 'With pieces in hand every empty square is a potential attack point. Pawns and golds in hand are the building blocks of mating nets.'],
      ['The tokin is valuable', 'A promoted pawn attacks like a gold, yet if captured only gives the opponent a pawn. Look for ways to get pawns into the enemy camp.'],
      ['Count exchanges carefully', "Unlike chess, captured pieces join the opponent's hand. Before you capture, ask what your opponent can do with that piece."],
    ],
    mistakesTitle: 'Common mistakes',
    mistakes: [
      'Attacking before castling, leaving the king on its starting square.',
      'Trying to drop a second pawn in the same file (nifu).',
      'Leaving the bishop diagonal open so the opponent can trade and drop a bishop.',
      'Advancing knights and lances too early: they can never go back.',
      "Forgetting the opponent's pieces in hand — check them every move.",
    ],
    cta: 'Practise with puzzles',
  },
};

export default function AcademyPage() {
  const { lang, to, t } = useLang();
  const c = C[lang];

  return (
    <>
      <Seo
        title={c.title}
        description={c.seoDesc}
        jsonLd={breadcrumbLd([{ name: t('brand'), path: to('home') }, { name: c.title, path: to('academy') }])}
      />
      <PageHeader title={c.title} lead={c.lead} crumbs={[{ label: c.title }]} />
      <div className="container">
        <section className="section pt-2">
          <div className="section-head">
            <h2 className="section-title" style={{ '--c': 'var(--c-academy)' }}>{c.pathTitle}</h2>
          </div>
          <div className="row g-3">
            {c.path.map(([title, desc, key], i) => (
              <div className="col-12 col-sm-6 col-lg-4" key={title}>
                <Link to={to(key)} className="game-card" style={{ '--c': 'var(--c-academy)' }}>
                  <span className="icon">{i + 1}</span>
                  <h3>{title}</h3>
                  <p>{desc}</p>
                  <span className="link-arrow">{t('common.readMore')}</span>
                </Link>
              </div>
            ))}
          </div>
        </section>

        <section className="section pt-0">
          <div className="section-head">
            <h2 className="section-title" style={{ '--c': 'var(--c-learn)' }}>{c.conceptsTitle}</h2>
          </div>
          <div className="row g-3">
            {c.concepts.map(([title, text]) => (
              <div className="col-12 col-lg-6" key={title}>
                <article className="panel prose h-100">
                  <h3 className="h4">{title}</h3>
                  <p className="mb-0">{text}</p>
                </article>
              </div>
            ))}
          </div>
        </section>

        <section className="panel prose" style={{ borderTop: '4px solid var(--c-play)' }}>
          <h2>{c.mistakesTitle}</h2>
          <ul>{c.mistakes.map((m) => <li key={m}>{m}</li>)}</ul>
          <Link to={to('puzzles')} className="btn btn-ink">{c.cta}</Link>
        </section>
      </div>
    </>
  );
}
